// Shared datetime boundary helpers:
// - Admin forms use local `datetime-local` inputs.
// - API payloads must send UTC ISO timestamps.
export const toDateTimeLocalInput = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    const trimmed = value.trim();
    const hasExplicitTimezone = /[zZ]$|[+-]\d{2}:\d{2}$/.test(trimmed);
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return `${trimmed}T00:00`;
    }

    if (!hasExplicitTimezone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 16);
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }

    const offsetMs = parsed.getTimezoneOffset() * 60000;
    const local = new Date(parsed.getTime() - offsetMs);
    return local.toISOString().slice(0, 16);
};

export const toUtcIsoString = (value?: string | null): string | null => {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return parsed.toISOString();
};

export const toDateInput = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
    }

    const hasExplicitTimezone = /[zZ]$|[+-]\d{2}:\d{2}$/.test(trimmed);
    if (!hasExplicitTimezone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 10);
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }

    return parsed.toISOString().slice(0, 10);
};

export const toUtcDateString = (value?: string | null): string | null => {
    if (!value) {
        return null;
    }

    const parsed = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return parsed.toISOString();
};

export const splitLocalDateTime = (value?: string | null): { date: string; time: string } => {
    const normalized = toDateTimeLocalInput(value);
    if (!normalized) {
        return { date: '', time: '' };
    }

    const [datePart, timePart] = normalized.split('T');
    return {
        date: datePart ?? '',
        time: (timePart ?? '').slice(0, 5)
    };
};

export const combineLocalDateTime = (date: string, time?: string | null): string => {
    if (!date) {
        return '';
    }

    const normalizedTime = (time ?? '').trim();
    const safeTime = /^\d{2}:\d{2}$/.test(normalizedTime) ? normalizedTime : '00:00';
    return `${date}T${safeTime}`;
};
